import { Delegate } from './delegate.js';

/**
 * Replaces Swiper's scaleX progress fill so both ends keep pill border-radius.
 * @param {import('swiper').Swiper} swiper
 * @param {ParentNode | null | undefined} root
 * @returns {() => void}
 */
function bindRoundedProgressbarFill(swiper, root) {
  const track =
    swiper.pagination?.el ||
    root?.querySelector('.swiper-pagination-progressbar') ||
    root?.querySelector('.swiper-pagination');
  if (!swiper || !track) return () => {};

  track.classList.add('swiper-pagination-progressbar--rounded-fill');

  const sync = () => {
    const total = swiper.slides?.length ?? 1;
    const ratio = Math.max(0, Math.min(1, (swiper.activeIndex + 1) / total));
    track.style.setProperty('--progressbar-fill-ratio', `${ratio * 100}%`);
    track.style.setProperty('--progressbar-fill-speed', `${swiper.params?.speed ?? 0}ms`);
  };

  sync();
  swiper.on('paginationRender', sync);
  swiper.on('paginationUpdate', sync);
  swiper.on('resize', sync);
  swiper.on('slideChange', sync);

  return () => {
    swiper.off('paginationRender', sync);
    swiper.off('paginationUpdate', sync);
    swiper.off('resize', sync);
    swiper.off('slideChange', sync);
    track.classList.remove('swiper-pagination-progressbar--rounded-fill');
    track.style.removeProperty('--progressbar-fill-ratio');
    track.style.removeProperty('--progressbar-fill-speed');
  };
}

if (!customElements.get('save-with-kits-cards')) {
  customElements.define(
    'save-with-kits-cards',
    class SaveWithKitsCards extends HTMLElement {
      connectedCallback() {
        this.delegate = new Delegate(this);
        this._raf = requestAnimationFrame(() => {
          this._raf = null;
          this.init();
        });
      }

      disconnectedCallback() {
        this.delegate?.destroy();
        this.delegate = null;
        cancelAnimationFrame(this._raf);
        this.destroy();
      }

      init() {
        if (typeof FoxTheme === 'undefined' || !FoxTheme.Carousel) {
          if (this._raf) return;
          this._raf = requestAnimationFrame(() => {
            this._raf = null;
            if (this.isConnected) this.init();
          });
          return;
        }

        const wrap = this.querySelector('.product__save-with-kits-swiper');
        const track = this.querySelector('.product__save-with-kits-track');
        if (!wrap || !track) return;

        const slideCount = track.querySelectorAll('.swiper-slide').length;
        if (slideCount === 0) return;

        if (this.carousel?.slider) {
          return;
        }

        wrap.classList.add('swiper', 'swiper-equal-height', 'overflow-visible');
        track.classList.add('swiper-wrapper');
        track.classList.remove('f-grid');

        const sliderOptions = this.parseSliderOptions();
        const peekSettings = sliderOptions.peek || {};
        delete sliderOptions.peek;

        const measuredWidth = Math.round(wrap.getBoundingClientRect().width || wrap.clientWidth || 300);
        const containerWidth = Math.max(measuredWidth, 200);
        const isMobile = window.matchMedia('(max-width: 767.98px)').matches; // matchMedia: legacy theme has no responsive utility module.
        const peekBase = isMobile ? (peekSettings.mobileBase ?? 1.315) : (peekSettings.desktopBase ?? 1.12);
        const widthAdjustment = isMobile
          ? (peekSettings.mobileWidthAdjustment ?? 18)
          : (peekSettings.desktopWidthAdjustment ?? 3);
        const paginationEl = this.querySelector(sliderOptions.pagination?.el || '.product__save-with-kits-nav .swiper-pagination');

        /**
         * Convert the desired "peek" ratio into a Swiper slidesPerView value.
         * targetSlideWidth starts from the configured card width, then narrows
         * it slightly so part of the next product card remains visible.
         */
        const targetSlideWidth = containerWidth / peekBase - widthAdjustment;
        const slidesPerView = slideCount > 1 ? containerWidth / targetSlideWidth : 1;
        const opts = {
          ...sliderOptions,
          slidesPerView,
        };

        if (slideCount > 1 && paginationEl) {
          opts.pagination = {
            ...sliderOptions.pagination,
            el: paginationEl,
          };
        } else {
          opts.pagination = false;
        }

        this.carousel = new window.FoxTheme.Carousel(wrap, opts, [FoxTheme.Swiper.Mousewheel]);
        this.carousel.init();
        this.unbindProgressbarFill = bindRoundedProgressbarFill(
          this.carousel.slider,
          this.closest('.product__save-with-kits-popup')
        );
      }

      /** Parses the Liquid-authored slider config from the HTML data attribute. */
      parseSliderOptions() {
        try {
          return JSON.parse(this.dataset.sliderOptions || '{}');
        } catch (error) {
          return {};
        }
      }

      destroy() {
        this.unbindProgressbarFill?.();
        this.unbindProgressbarFill = null;
        if (typeof this.carousel !== 'object' || !this.carousel?.slider) return;
        try {
          this.carousel.slider.destroy(true, true);
        } catch (_) {
          /* noop */
        }
        this.carousel = null;

        const wrap = this.querySelector('.product__save-with-kits-swiper');
        const track = this.querySelector('.product__save-with-kits-track');
        if (wrap) {
          wrap.classList.remove('swiper', 'swiper-equal-height', 'overflow-visible');
        }
        if (track) {
          track.classList.remove('swiper-wrapper');
          track.classList.add('f-grid', 'f-grid--gap-2xs');
        }
      }
    }
  );
}
